import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { generateEntityCode } from '@erp/domain/numbering';
import { queryClient } from '@/lib/query-client';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { useNativeBridge } from '@/lib/native-context';
import { normalizeQueryFilter } from '@/lib/normalize-query-filter';
import { listCacheFallback } from '@/lib/list-cache-fallback';
import { bumpOfflineCounter, getOfflineCounter, useOfflineNextCode } from '@/lib/offline-counter';
import type { Article, ArticleCategory, ArticleFilters, CompanySettings, CreateArticleInput, UpdateArticleInput } from '@erp/data-access/interfaces';

/** Read cached company settings from outside React (no hooks) — used by
 *  the optimistic builder which runs synchronously inside a mutation. */
function readSettingsFromCache(): CompanySettings | null {
  return queryClient.getQueryData<CompanySettings>(['company-settings']) ?? null;
}

function readCachedArticle(qc: ReturnType<typeof useQueryClient>, id: string, key: readonly unknown[]): Partial<Article> {
  const direct = qc.getQueryData<Article>([...key, id]);
  if (direct) return direct;
  for (const [, list] of qc.getQueriesData<Article[]>({ queryKey: key })) {
    if (Array.isArray(list)) {
      const found = list.find((a) => a.id === id);
      if (found) return found;
    }
  }
  return { id };
}

const OFFLINE_PREFIX = 'OFFLINE-';

const ARTICLES_KEY = ['articles'] as const;
const CATEGORIES_KEY = ['article-categories'] as const;

export function useArticles(filters?: ArticleFilters) {
  const ds = useDataService();
  // Collapse all-empty filter objects to `undefined` so the unfiltered
  // page render hits the warm cache populated by usePrewarmCache.
  const normalized = normalizeQueryFilter(filters);
  return useQuery({
    queryKey: [...ARTICLES_KEY, normalized],
    queryFn: () => ds.articles.list(normalized),
    placeholderData: keepPreviousData,
  });
}

export function useArticleCount(filters?: ArticleFilters) {
  const ds = useDataService();
  const normalized = normalizeQueryFilter(filters);
  return useQuery({
    queryKey: [...ARTICLES_KEY, 'count', normalized],
    queryFn: () => ds.articles.count(normalized),
    placeholderData: keepPreviousData,
  });
}

export function useNextArticleCode(enabled: boolean) {
  const ds = useDataService();
  const bridge = useNativeBridge();
  // Always compute the offline equivalent — `companySettings.articleCodeNext`
  // (server counter) + local offset (number of pending offline creates).
  // We prefer this value when offline so the form keeps incrementing
  // sensibly even when the server's `peekNextCode` is unreachable AND
  // any cached previous peek is now stale (because we've created N
  // records locally that the server doesn't know about yet).
  const offlineCode = useOfflineNextCode('article');
  const query = useQuery({
    queryKey: [...ARTICLES_KEY, 'next-code'],
    queryFn: () => ds.articles.peekNextCode(),
    enabled,
    staleTime: 0,
    placeholderData: () => offlineCode ?? undefined,
  });
  const offline = !bridge.network.isOnline();
  // Online: trust the server peek (fresh + authoritative).
  // Offline: trust the local computation (server peek may be stale OR
  // ahead by some N that doesn't account for our pending creates).
  const data = offline ? (offlineCode ?? query.data) : (query.data ?? offlineCode);
  return { ...query, data } as typeof query;
}

export function useNextArticleBarcode(enabled: boolean) {
  const ds = useDataService();
  const bridge = useNativeBridge();
  const offlineBarcode = useOfflineNextCode('barcode');
  const query = useQuery({
    queryKey: [...ARTICLES_KEY, 'next-barcode'],
    queryFn: () => ds.articles.peekNextBarcode(),
    enabled,
    staleTime: 0,
    placeholderData: () => offlineBarcode ?? undefined,
  });
  const offline = !bridge.network.isOnline();
  const data = offline ? (offlineBarcode ?? query.data) : (query.data ?? offlineBarcode);
  return { ...query, data } as typeof query;
}

export function useArticle(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...ARTICLES_KEY, id],
    queryFn: () => ds.articles.getById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<Article>(qc, ARTICLES_KEY, id),
  });
}

export function useCreateArticle() {
  const ds = useDataService();
  return useResilientMutation<CreateArticleInput, Article>({
    entity: 'articles',
    op: 'create',
    invalidate: [ARTICLES_KEY],
    online: (input) => ds.articles.create(input),
    optimistic: optimisticArticle,
    applyOptimistic: (qc, newArticle, input) => {
      qc.setQueriesData<Article[]>({ queryKey: ARTICLES_KEY }, (old) =>
        Array.isArray(old) ? [...old, newArticle].sort((a, b) => a.name.localeCompare(b.name)) : old,
      );
      // Reserve the next code/barcode locally so a SECOND offline create
      // doesn't pick the same number. Only bump the counter for fields
      // the user left to autoincrement (i.e. didn't type themselves).
      if (!input.code) bumpOfflineCounter('article');
      if (!input.barcode && input.autoGenerateBarcode !== false) bumpOfflineCounter('barcode');
    },
  });
}

/**
 * Build a complete `Article` from a `CreateArticleInput` so list pages
 * that expect every numeric/string field (formatPrice, sort, filter)
 * never crash on a missing prop.
 *
 * Code + barcode strategy:
 *   - If the user typed a value, use it verbatim.
 *   - Otherwise compute the offline-aware "next code" — `prefix-NNNN`
 *     using the cached company_settings counter + the local offline
 *     offset. Same format the server would have produced. The user
 *     never sees "OFFLINE-xyz" on screen.
 *   - Falls back to OFFLINE-XXX only when company-settings haven't loaded
 *     yet (true cold start with no cache) — extreme edge case.
 */
function optimisticArticle(input: CreateArticleInput, clientId: string): Article {
  const now = new Date().toISOString();
  const settings = readSettingsFromCache();
  const offsetCode = getOfflineCounter('article');
  const offsetBarcode = getOfflineCounter('barcode');
  const codeFromOffline = settings
    ? generateEntityCode(settings.articleCodePrefix, settings.articleCodeNext + offsetCode, settings.articleCodePadding)
    : OFFLINE_PREFIX + clientId.slice(0, 6).toUpperCase();
  const barcodeFromOffline = settings
    ? generateEntityCode(settings.barcodePrefix, settings.barcodeNext + offsetBarcode, settings.barcodePadding)
    : null;
  return {
    id: clientId,
    code: input.code?.trim() || codeFromOffline,
    barcode: input.barcode ?? (input.autoGenerateBarcode !== false ? barcodeFromOffline : null),
    name: input.name,
    description: input.description ?? null,
    categoryId: input.categoryId ?? null,
    family: input.family ?? null,
    subFamily: input.subFamily ?? null,
    brand: input.brand ?? null,
    unit: input.unit ?? 'pcs',
    purchasePriceHt: input.purchasePriceHt ?? 0,
    supplierDiscount: input.supplierDiscount ?? 0,
    salePriceHt: input.salePriceHt ?? 0,
    salePriceTtc: input.salePriceTtc ?? 0,
    vatRate: input.vatRate ?? 19,
    stockMin: input.stockMin ?? 0,
    initialStock: input.initialStock ?? 0,
    images: input.images ?? [],
    imageUrl: input.imageUrl ?? null,
    isActive: input.isActive ?? true,
    isInvoiceable: input.isInvoiceable ?? true,
    createdAt: now,
    updatedAt: now,
  };
}

export function useUpdateArticle() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useResilientMutation<{ id: string; input: UpdateArticleInput }, Article>({
    entity: 'articles',
    op: 'update',
    invalidate: [ARTICLES_KEY],
    online: ({ id, input }) => ds.articles.update(id, input),
    // Merge with cached row so untouched fields stay populated. If the
    // cached row is missing (StrictMode / race / cleared cache), fall
    // back to a fully-defaulted optimistic row so downstream components
    // never see undefined numeric fields.
    optimistic: ({ id, input }) => {
      const cached = readCachedArticle(qc, id, ARTICLES_KEY);
      const fallback = optimisticArticle({ name: cached.name ?? input.name ?? '(édité)', ...input }, id);
      return { ...fallback, ...cached, ...input, id } as Article;
    },
    applyOptimistic: (qc, updated, { id }) => {
      qc.setQueriesData<Article[]>({ queryKey: ARTICLES_KEY }, (old) =>
        Array.isArray(old) ? old.map((a) => (a.id === id ? updated : a)) : old,
      );
      qc.setQueryData<Article>([...ARTICLES_KEY, id], updated);
    },
  });
}

export function useDeleteArticle() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'articles',
    op: 'delete',
    invalidate: [ARTICLES_KEY],
    online: (id) => ds.articles.delete(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _result, id) => {
      qc.setQueriesData<Article[]>({ queryKey: ARTICLES_KEY }, (old) =>
        Array.isArray(old) ? old.filter((a) => a.id !== id) : old,
      );
      qc.removeQueries({ queryKey: [...ARTICLES_KEY, id] });
    },
  });
}

export function useArticleCategories() {
  const ds = useDataService();
  return useQuery({
    queryKey: CATEGORIES_KEY,
    queryFn: () => ds.articleCategories.list(),
  });
}

export function useCreateCategory() {
  const ds = useDataService();
  type In = { name: string; parentId?: string; imageUrl?: string };
  type Out = ArticleCategory;
  return useResilientMutation<In, Out>({
    entity: 'article_categories',
    op: 'create',
    invalidate: [CATEGORIES_KEY],
    online: (input) => ds.articleCategories.create(input) as Promise<Out>,
    optimistic: (input, clientId) => ({
      id: clientId,
      name: input.name,
      parentId: input.parentId ?? null,
      imageUrl: input.imageUrl ?? null,
    }),
    applyOptimistic: (qc, cat) => {
      qc.setQueriesData<Out[]>({ queryKey: CATEGORIES_KEY }, (old) =>
        Array.isArray(old) && !old.some((c) => c.id === cat.id) ? [...old, cat] : old,
      );
    },
  });
}

export function useUpdateCategory() {
  const ds = useDataService();
  type In = { id: string; input: { name?: string; parentId?: string; imageUrl?: string } };
  type Out = ArticleCategory;
  return useResilientMutation<In, Out>({
    entity: 'article_categories',
    op: 'update',
    invalidate: [CATEGORIES_KEY],
    online: ({ id, input }) => ds.articleCategories.update(id, input) as Promise<Out>,
    optimistic: ({ id, input }) => ({
      id,
      name: input.name ?? '(modifie hors-ligne)',
      parentId: input.parentId ?? null,
      imageUrl: input.imageUrl ?? null,
    }),
    applyOptimistic: (qc, _r, { id, input }) => {
      qc.setQueriesData<Out[]>({ queryKey: CATEGORIES_KEY }, (old) =>
        Array.isArray(old) ? old.map((c) => (c.id === id ? { ...c, ...input } : c)) : old,
      );
    },
  });
}

export function useDeleteCategory() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'article_categories',
    op: 'delete',
    invalidate: [CATEGORIES_KEY],
    online: (id) => ds.articleCategories.delete(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<{ id: string }[]>({ queryKey: CATEGORIES_KEY }, (old) =>
        Array.isArray(old) ? old.filter((c) => c.id !== id) : old,
      );
    },
  });
}

// ── Families ──────────────────────────────────────────────────────

const FAMILIES_KEY = ['article-families'] as const;

export function useArticleFamilies() {
  const ds = useDataService();
  return useQuery({ queryKey: FAMILIES_KEY, queryFn: () => ds.articleFamilies.list() });
}

export function useCreateFamily() {
  const ds = useDataService();
  type Out = { id: string; name: string };
  return useResilientMutation<string, Out>({
    entity: 'article_families',
    op: 'create',
    invalidate: [FAMILIES_KEY],
    online: (name) => ds.articleFamilies.create(name) as Promise<Out>,
    optimistic: (name, clientId) => ({ id: clientId, name }),
    applyOptimistic: (qc, fam) => {
      qc.setQueriesData<Out[]>({ queryKey: FAMILIES_KEY }, (old) =>
        Array.isArray(old) && !old.some((f) => f.id === fam.id) ? [...old, fam] : old,
      );
    },
  });
}

export function useUpdateFamily() {
  const ds = useDataService();
  type Out = { id: string; name: string };
  return useResilientMutation<{ id: string; name: string }, Out>({
    entity: 'article_families',
    op: 'update',
    invalidate: [FAMILIES_KEY],
    online: ({ id, name }) => ds.articleFamilies.update(id, { name }) as Promise<Out>,
    optimistic: ({ id, name }) => ({ id, name }),
    applyOptimistic: (qc, _r, { id, name }) => {
      qc.setQueriesData<Out[]>({ queryKey: FAMILIES_KEY }, (old) =>
        Array.isArray(old) ? old.map((f) => (f.id === id ? { ...f, name } : f)) : old,
      );
    },
  });
}

export function useDeleteFamily() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'article_families',
    op: 'delete',
    invalidate: [FAMILIES_KEY],
    online: (id) => ds.articleFamilies.delete(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<{ id: string }[]>({ queryKey: FAMILIES_KEY }, (old) =>
        Array.isArray(old) ? old.filter((f) => f.id !== id) : old,
      );
    },
  });
}

// ── Brands ────────────────────────────────────────────────────────

const BRANDS_KEY = ['article-brands'] as const;

export function useArticleBrands() {
  const ds = useDataService();
  return useQuery({ queryKey: BRANDS_KEY, queryFn: () => ds.articleBrands.list() });
}

export function useCreateBrand() {
  const ds = useDataService();
  type In = { name: string; imageUrl?: string };
  type Out = { id: string; name: string };
  return useResilientMutation<In, Out>({
    entity: 'article_brands',
    op: 'create',
    invalidate: [BRANDS_KEY],
    online: (input) => ds.articleBrands.create(input) as Promise<Out>,
    optimistic: (input, clientId) => ({ id: clientId, name: input.name }),
    applyOptimistic: (qc, brand) => {
      qc.setQueriesData<Out[]>({ queryKey: BRANDS_KEY }, (old) =>
        Array.isArray(old) && !old.some((b) => b.id === brand.id) ? [...old, brand] : old,
      );
    },
  });
}

export function useUpdateBrand() {
  const ds = useDataService();
  type In = { id: string; name?: string; imageUrl?: string };
  type Out = { id: string; name: string };
  return useResilientMutation<In, Out>({
    entity: 'article_brands',
    op: 'update',
    invalidate: [BRANDS_KEY],
    online: ({ id, ...input }) => ds.articleBrands.update(id, input) as Promise<Out>,
    optimistic: ({ id, name }) => ({ id, name: name ?? '(édité hors-ligne)' }),
    applyOptimistic: (qc, _r, { id, name }) => {
      qc.setQueriesData<Out[]>({ queryKey: BRANDS_KEY }, (old) =>
        Array.isArray(old) ? old.map((b) => (b.id === id ? { ...b, name: name ?? b.name } : b)) : old,
      );
    },
  });
}

export function useDeleteBrand() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'article_brands',
    op: 'delete',
    invalidate: [BRANDS_KEY],
    online: (id) => ds.articleBrands.delete(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<{ id: string }[]>({ queryKey: BRANDS_KEY }, (old) =>
        Array.isArray(old) ? old.filter((b) => b.id !== id) : old,
      );
    },
  });
}
